// Client\client-vite\src\components\Navbars\DealerNavbar.tsx
import * as React from "react"

import { CircleCheckIcon, CircleHelpIcon, CircleIcon, LogOut, Settings, User, Users2 } from "lucide-react"

import {
    NavigationMenu,
    NavigationMenuContent,
    NavigationMenuItem,
    NavigationMenuLink,
    NavigationMenuList,
    NavigationMenuTrigger,
    navigationMenuTriggerStyle,
} from "@/components/ui/navigation-menu"
import { Link, useNavigate } from "react-router-dom"
import { ThemeModeToggle } from "../Toggles/ThemeModeToggle"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "../ui/dropdown-menu"
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar"
import { SidebarTrigger } from "../ui/sidebar"
import { getCurrentDealer, logoutDealer, type DealerAuthUser } from "@/Calls/callDealerAuth"
import { toast } from "sonner"
import { useEffect, useState } from "react"


const components: { title: string; to: string; description: string }[] = [
    {
        title: "Alert Dialog",
        to: "/docs/primitives/alert-dialog",
        description:
            "A modal dialog that interrupts the user with important content and expects a response.",
    },
    {
        title: "Hover Card",
        to: "/docs/primitives/hover-card",
        description:
            "For sighted users to preview content available behind a link.",
    },
    {
        title: "Progress",
        to: "/docs/primitives/progress",
        description:
            "Displays an indicator showing the completion progress of a task, typically displayed as a progress bar.",
    },
    {
        title: "Scroll-area",
        to: "/docs/primitives/scroll-area",
        description: "Visually or semantically separates content.",
    },
    {
        title: "Tabs",
        to: "/docs/primitives/tabs",
        description:
            "A set of layered sections of content—known as tab panels—that are displayed one at a time.",
    },
    {
        title: "Tooltip",
        to: "/docs/primitives/tooltip",
        description:
            "A popup that displays information related to an element when the element receives keyboard focus or the mouse hovers over it.",
    },
]

export const DealerNavbar = () => {


        const navigate = useNavigate()

        const [dealer, setDealer] = useState<DealerAuthUser | null>(null);

        async function handleLogout(): Promise<void> {
        try {
            await logoutDealer();
            toast("👋 Logged out successfully");
            navigate("/dealer/login", { replace: true });
        } catch (error) {
            if (error instanceof Error) {
                console.error("Logout failed:", error);
                toast("❌ Logout failed", {
                    description: error.message || "Please try again.",
                });
            } else {
                console.error("Logout failed with unknown error:", error);
                toast("❌ Logout failed", {
                    description: "An unexpected error occurred.",
                });
            }
        }
    }

     useEffect(() => {
            (async () => {
                const current = await getCurrentDealer();
                setDealer(current);
            })();
        }, []);

    return (
        <div className="w-full bg-background border-b">
            <nav className="w-full flex items-center justify-between px-4 py-2">

                {/* LEFT */}
                <div>
                    {/* CollapseButtons */}
                    <SidebarTrigger />
                </div>

                
                {/* CENTER MENU */}
                <div className="flex-1 flex justify-center">
                    <NavigationMenu viewport={false} className="w-auto">
                        <NavigationMenuList className="flex gap-6">
                            <NavigationMenuItem>
                                <NavigationMenuTrigger>Home</NavigationMenuTrigger>
                                <NavigationMenuContent>
                                    <ul className="grid gap-2 md:w-[400px] lg:w-[500px] lg:grid-cols-[.75fr_1fr]">
                                        <li className="row-span-3">
                                            <NavigationMenuLink asChild>
                                                <Link
                                                    className="from-muted/50 to-muted flex h-full w-full flex-col justify-end rounded-md bg-linear-to-b p-6 no-underline outline-hidden select-none focus:shadow-md"
                                                    to="/"
                                                >
                                                    <div className="mt-4 mb-2 text-lg font-medium">
                                                        shadcn/ui
                                                    </div>
                                                    <p className="text-muted-foreground text-sm leading-tight">
                                                        Beautifully designed components built with Tailwind CSS.
                                                    </p>
                                                </Link>
                                            </NavigationMenuLink>
                                        </li>
                                        <ListItem to="/docs" title="Introduction">
                                            Re-usable components built using Radix UI and Tailwind CSS.
                                        </ListItem>
                                        <ListItem to="/docs/installation" title="Installation">
                                            How to install dependencies and structure your app.
                                        </ListItem>
                                        <ListItem to="/docs/primitives/typography" title="Typography">
                                            Styles for headings, paragraphs, lists...etc
                                        </ListItem>
                                    </ul>
                                </NavigationMenuContent>
                            </NavigationMenuItem>
                            <NavigationMenuItem>
                                <NavigationMenuTrigger>Components</NavigationMenuTrigger>
                                <NavigationMenuContent>
                                    <ul className="grid w-[400px] gap-2 md:w-[500px] md:grid-cols-2 lg:w-[600px]">
                                        {components.map((component) => (
                                            <ListItem
                                                key={component.title}
                                                title={component.title}
                                                to={component.to}
                                            >
                                                {component.description}
                                            </ListItem>
                                        ))}
                                    </ul>
                                </NavigationMenuContent>
                            </NavigationMenuItem>
                            <NavigationMenuItem>
                                <NavigationMenuLink asChild className={navigationMenuTriggerStyle()}>
                                    <Link to="/docs">Docs</Link>
                                </NavigationMenuLink>
                            </NavigationMenuItem>
                            <NavigationMenuItem>
                                <NavigationMenuTrigger>List</NavigationMenuTrigger>
                                <NavigationMenuContent>
                                    <ul className="grid w-[300px] gap-4">
                                        <li>
                                            <NavigationMenuLink asChild>
                                                <Link to="#">
                                                    <div className="font-medium">Components</div>
                                                    <div className="text-muted-foreground">
                                                        Browse all components in the library.
                                                    </div>
                                                </Link>
                                            </NavigationMenuLink>
                                            <NavigationMenuLink asChild>
                                                <Link to="#">
                                                    <div className="font-medium">Documentation</div>
                                                    <div className="text-muted-foreground">
                                                        Learn how to use the library.
                                                    </div>
                                                </Link>
                                            </NavigationMenuLink>
                                            <NavigationMenuLink asChild>
                                                <Link to="#">
                                                    <div className="font-medium">Blog</div>
                                                    <div className="text-muted-foreground">
                                                        Read our latest blog posts.
                                                    </div>
                                                </Link>
                                            </NavigationMenuLink>
                                        </li>
                                    </ul>
                                </NavigationMenuContent>
                            </NavigationMenuItem>
                            <NavigationMenuItem>
                                <NavigationMenuTrigger>Dashboards</NavigationMenuTrigger>
                                <NavigationMenuContent>
                                    <ul className="grid w-[200px] gap-4">
                                        <li>
                                            <NavigationMenuLink asChild>
                                                <Link to="/admin/dashboard">Admin Dashboard</Link>
                                            </NavigationMenuLink>
                                            <NavigationMenuLink asChild>
                                                <Link to="/dealers/dashboard">Dealers Dashboard</Link>
                                            </NavigationMenuLink>
                                            <NavigationMenuLink asChild>
                                                <Link to="/customer/portal">Customer Portal</Link>
                                            </NavigationMenuLink>
                                            <NavigationMenuLink asChild>
                                                <Link to="/sales/dashboard">Sales Dashboard</Link>
                                            </NavigationMenuLink>
                                            <NavigationMenuLink asChild>
                                                <Link to="/finance/dashboard">Finance Dashboard</Link>
                                            </NavigationMenuLink>
                                            <NavigationMenuLink asChild>
                                                <Link to="/bdc/dashboard">BDC Dashboard</Link>
                                            </NavigationMenuLink>
                                            <NavigationMenuLink asChild>
                                                <Link to="/service/dashboard">Service Dashboard</Link>
                                            </NavigationMenuLink>
                                            <NavigationMenuLink asChild>
                                                <Link to="#">Parts Login</Link>
                                            </NavigationMenuLink>
                                        </li>
                                    </ul>
                                </NavigationMenuContent>
                            </NavigationMenuItem>
                            <NavigationMenuItem>
                                <NavigationMenuTrigger>Logins</NavigationMenuTrigger>
                                <NavigationMenuContent>
                                    <ul className="grid w-[200px] gap-4">
                                        <li>
                                            <NavigationMenuLink asChild>
                                                <Link to="/admin/login">Admin Login</Link>
                                            </NavigationMenuLink>
                                            <NavigationMenuLink asChild>
                                                <Link to="/dealers/login">Dealers Login</Link>
                                            </NavigationMenuLink>
                                            <NavigationMenuLink asChild>
                                                <Link to="/customer/login">Customer Login</Link>
                                            </NavigationMenuLink>
                                            <NavigationMenuLink asChild>
                                                <Link to="/sales/login">Sales Login</Link>
                                            </NavigationMenuLink>
                                            <NavigationMenuLink asChild>
                                                <Link to="/finance/login">Finance Login</Link>
                                            </NavigationMenuLink>
                                            <NavigationMenuLink asChild>
                                                <Link to="/bdc/login">BDC Login</Link>
                                            </NavigationMenuLink>
                                            <NavigationMenuLink asChild>
                                                <Link to="/service/login">Service Login</Link>
                                            </NavigationMenuLink>
                                            <NavigationMenuLink asChild>
                                                <Link to="/parts/login">Parts Login</Link>
                                            </NavigationMenuLink>
                                        </li>
                                    </ul>
                                </NavigationMenuContent>
                            </NavigationMenuItem>
                            <NavigationMenuItem>
                                <NavigationMenuTrigger>With Icon</NavigationMenuTrigger>
                                <NavigationMenuContent>
                                    <ul className="grid w-[200px] gap-4">
                                        <li>
                                            <NavigationMenuLink asChild>
                                                <Link to="#" className="flex-row items-center gap-2">
                                                    <CircleHelpIcon />
                                                    Backlog
                                                </Link>
                                            </NavigationMenuLink>
                                            <NavigationMenuLink asChild>
                                                <Link to="#" className="flex-row items-center gap-2">
                                                    <CircleIcon />
                                                    To Do
                                                </Link>
                                            </NavigationMenuLink>
                                            <NavigationMenuLink asChild>
                                                <Link to="#" className="flex-row items-center gap-2">
                                                    <CircleCheckIcon />
                                                    Done
                                                </Link>
                                            </NavigationMenuLink>
                                        </li>
                                    </ul>
                                </NavigationMenuContent>
                            </NavigationMenuItem>
                        </NavigationMenuList>
                    </NavigationMenu>
                </div>

                {/* RIGHT */}
                <div className="flex items-center gap-4 ml-auto">
                    <ThemeModeToggle />

                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Avatar className="cursor-pointer">
                                <AvatarImage
                                    src="/images/logo.png"
                                    className="object-contain"
                                 />
                                <AvatarFallback><span className="ml-2">
                                        {dealer?.company || dealer?.username || dealer?.email || "Dealer"}
                                    </span></AvatarFallback>
                            </Avatar>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" sideOffset={10}>
                            <DropdownMenuLabel>My Account</DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem>
                                <User className="h-4 w-4 mr-2" />
                                Profile
                            </DropdownMenuItem>
                            <DropdownMenuItem>
                                <Settings className="h-4 w-4 mr-2" />
                                Settings
                            </DropdownMenuItem>
                            <DropdownMenuItem>
                                <Users2 className="h-4 w-4 mr-2" />
                                Team
                            </DropdownMenuItem>
                            <DropdownMenuItem
                                    onClick={handleLogout}
                                    className="text-destructive cursor-pointer"
                                >
                                <LogOut className="h-4 w-4 mr-2" />
                                Logout
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </nav>
        </div>
    )
}

function ListItem({
    title,
    children,
    to,
    ...props
}: React.ComponentPropsWithoutRef<"li"> & { to: string }) {
    return (
        <li {...props}>
            <NavigationMenuLink asChild>
                <Link to={to}>
                    <div className="text-sm leading-none font-medium">{title}</div>
                    <p className="text-muted-foreground line-clamp-2 text-sm leading-snug">
                        {children}
                    </p>
                </Link>
            </NavigationMenuLink>
        </li>
    )
}


export default DealerNavbar